package er.prateek.crossly.game.crossword

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import er.prateek.crossly.data.model.Clue
import er.prateek.crossly.data.model.ClueDirection
import er.prateek.crossly.data.model.Puzzle
import er.prateek.crossly.data.model.SavedCellFill

data class CellPos(val row: Int, val col: Int)

enum class CellStatus { EMPTY, FILLED, CORRECT, INCORRECT, REVEALED, GIVEN }

class CrosswordCellState(
    val pos: CellPos,
    val isBlack: Boolean,
    val solution: Char?,
    val number: Int?,
    /** Pre-filled starter letter: correct from the outset and not editable. */
    val isGiven: Boolean = false,
) {
    var userChar: Char? by mutableStateOf(if (isGiven) solution else null)
    var status: CellStatus by mutableStateOf(if (isGiven) CellStatus.GIVEN else CellStatus.EMPTY)

    /** True once the cell holds the right letter, however it got there. */
    val isResolved: Boolean get() = userChar != null && userChar == solution
}

/**
 * Pure game-state machine for one crossword: fill/check/reveal/win-detection.
 * The solution grid and cell numbering are derived entirely from the authored
 * clue list (row/col/answer), not from re-parsing the grid string, so the
 * clue data is the single source of truth for what belongs in each cell.
 *
 * [givenCells] open pre-filled and locked (see [StarterLetters]). They are free:
 * the cursor steps over them, typing and deleting can't touch them, and they
 * never count as a hint.
 */
class CrosswordEngine(
    val puzzle: Puzzle,
    givenCells: Set<CellPos> = emptySet(),
) {
    val width = puzzle.width
    val height = puzzle.height

    private val cellByPos: Map<CellPos, CrosswordCellState> = buildCells(puzzle, givenCells)
    val cells: List<CrosswordCellState> = cellByPos.values.toList()

    var selected: CellPos? by mutableStateOf(cellByPos.values.firstOrNull { !it.isBlack }?.pos)
        private set
    var direction: ClueDirection by mutableStateOf(ClueDirection.ACROSS)
        private set
    var hintsUsed: Int by mutableStateOf(0)
        private set
    /** Letters revealed via [revealCell] specifically -- what the reveal-letter daily/per-puzzle caps count. */
    var lettersRevealed: Int by mutableStateOf(0)
        private set
    var mistakes: Int by mutableStateOf(0)
        private set
    var isSolved: Boolean by mutableStateOf(false)
        private set

    fun cellAt(pos: CellPos): CrosswordCellState? = cellByPos[pos]

    fun selectCell(pos: CellPos) {
        val cell = cellByPos[pos] ?: return
        if (cell.isBlack) return
        if (selected == pos) {
            direction = direction.opposite()
        } else {
            selected = pos
            if (!hasWordInDirection(pos, direction)) {
                direction = direction.opposite()
            }
        }
    }

    private fun hasWordInDirection(pos: CellPos, dir: ClueDirection): Boolean {
        val (dr, dc) = dir.delta()
        val before = cellByPos[CellPos(pos.row - dr, pos.col - dc)]
        val after = cellByPos[CellPos(pos.row + dr, pos.col + dc)]
        return (before != null && !before.isBlack) || (after != null && !after.isBlack)
    }

    fun inputLetter(letter: Char) {
        if (!letter.isLetter()) return
        val start = selected ?: return
        val startCell = cellByPos[start] ?: return
        if (startCell.isBlack) return

        // Typing while parked on a starter letter shouldn't swallow the keystroke:
        // the cursor hops to the next square the player is actually allowed to fill.
        val pos = if (startCell.isGiven) moveSelection(start, forward = true) ?: return else start
        val cell = cellByPos[pos] ?: return

        cell.userChar = letter.uppercaseChar()
        cell.status = CellStatus.FILLED
        moveSelection(pos, forward = true)
        refreshSolvedState()
    }

    /**
     * Drops [letter] straight onto [pos] — the drag-and-drop path.
     *
     * Unlike [inputLetter] the cursor doesn't have to be there first: the square
     * the player let go over becomes the cursor (facing whichever way it has a
     * word), takes the letter, and the cursor moves on so typing can continue
     * from where the drop landed.
     */
    fun placeLetterAt(pos: CellPos, letter: Char) {
        val cell = cellByPos[pos] ?: return
        if (cell.isBlack || cell.isGiven || !letter.isLetter()) return

        selected = pos
        if (!hasWordInDirection(pos, direction)) direction = direction.opposite()
        cell.userChar = letter.uppercaseChar()
        cell.status = CellStatus.FILLED
        moveSelection(pos, forward = true)
        refreshSolvedState()
    }

    /**
     * Takes a letter the player put down back off the board.
     *
     * Returns the letter that was removed so the UI can animate it home, or null
     * when there was nothing of the player's to remove — an empty square, or a
     * starter letter, which stays put.
     */
    fun clearLetterAt(pos: CellPos): Char? {
        val cell = cellByPos[pos] ?: return null
        if (cell.isBlack || cell.isGiven) return null
        val removed = cell.userChar ?: return null
        cell.userChar = null
        cell.status = CellStatus.EMPTY
        selected = pos
        return removed
    }

    fun deleteLetter() {
        val pos = selected ?: return
        val cell = cellByPos[pos] ?: return
        if (cell.isBlack) return
        if (cell.userChar != null && !cell.isGiven) {
            cell.userChar = null
            cell.status = CellStatus.EMPTY
        } else {
            moveSelection(pos, forward = false)?.let { prev ->
                cellByPos[prev]?.apply {
                    userChar = null
                    status = CellStatus.EMPTY
                }
            }
        }
    }

    /**
     * Steps one square along the current direction, skipping starter letters —
     * they are already correct, so stopping on them would just make the player
     * arrow past them by hand.
     */
    private fun moveSelection(from: CellPos, forward: Boolean): CellPos? {
        val (dr, dc) = direction.delta()
        val step = if (forward) 1 else -1
        var next = CellPos(from.row + dr * step, from.col + dc * step)
        while (true) {
            val cell = cellByPos[next] ?: return null
            if (cell.isBlack) return null
            if (!cell.isGiven) {
                selected = next
                return next
            }
            next = CellPos(next.row + dr * step, next.col + dc * step)
        }
    }

    fun checkCell(pos: CellPos) {
        val cell = cellByPos[pos] ?: return
        if (cell.isBlack || cell.isGiven || cell.userChar == null) return
        if (cell.userChar == cell.solution) {
            cell.status = CellStatus.CORRECT
        } else {
            cell.status = CellStatus.INCORRECT
            mistakes++
        }
    }

    fun checkGrid() {
        cells.filter { !it.isBlack && it.userChar != null }.forEach { checkCell(it.pos) }
    }

    fun revealSelectedCell() {
        selected?.let { revealCell(it) }
    }

    fun revealCell(pos: CellPos) {
        val cell = cellByPos[pos] ?: return
        // A starter letter is already correct — revealing it would charge a hint
        // for nothing.
        if (cell.isBlack || cell.isGiven) return
        cell.userChar = cell.solution
        cell.status = CellStatus.REVEALED
        hintsUsed++
        lettersRevealed++
        refreshSolvedState()
    }

    fun revealGrid() {
        cells.filter { !it.isBlack && !it.isGiven }.forEach {
            it.userChar = it.solution
            it.status = CellStatus.REVEALED
        }
        hintsUsed++
        isSolved = true
    }

    private fun refreshSolvedState() {
        isSolved = cells.filter { !it.isBlack }.all { it.userChar == it.solution }
    }

    fun clueAt(pos: CellPos, dir: ClueDirection): Clue? {
        val (dr, dc) = dir.delta()
        var r = pos.row
        var c = pos.col
        while (cellByPos[CellPos(r - dr, c - dc)]?.isBlack == false) {
            r -= dr
            c -= dc
        }
        val list = if (dir == ClueDirection.ACROSS) puzzle.cluesAcross else puzzle.cluesDown
        return list.firstOrNull { it.row == r && it.col == c }
    }

    fun snapshotFills(): List<SavedCellFill> =
        cells.mapNotNull { cell ->
            if (cell.isBlack || cell.isGiven || cell.userChar == null) null
            else SavedCellFill(cell.pos.row, cell.pos.col, cell.userChar.toString(), cell.status.name)
        }

    fun applySnapshot(
        fills: List<SavedCellFill>,
        hintsUsed: Int,
        lettersRevealed: Int,
        mistakes: Int,
        selectedRow: Int?,
        selectedCol: Int?,
        directionName: String,
    ) {
        fills.forEach { fill ->
            val cell = cellByPos[CellPos(fill.row, fill.col)] ?: return@forEach
            if (cell.isBlack || cell.isGiven) return@forEach
            val letter = fill.letter.firstOrNull()?.uppercaseChar() ?: return@forEach
            cell.userChar = letter
            cell.status = CellStatus.entries.find { it.name == fill.status } ?: CellStatus.FILLED
        }
        this.hintsUsed = hintsUsed.coerceAtLeast(0)
        this.lettersRevealed = lettersRevealed.coerceAtLeast(0)
        this.mistakes = mistakes.coerceAtLeast(0)
        if (selectedRow != null && selectedCol != null) {
            val pos = CellPos(selectedRow, selectedCol)
            if (cellByPos[pos]?.isBlack == false) selected = pos
        }
        direction = if (directionName == ClueDirection.DOWN.name) ClueDirection.DOWN else ClueDirection.ACROSS
        refreshSolvedState()
    }

    fun currentClue(): Clue? = selected?.let { clueAt(it, direction) }
}

private fun ClueDirection.delta(): Pair<Int, Int> =
    if (this == ClueDirection.ACROSS) 0 to 1 else 1 to 0

private fun ClueDirection.opposite(): ClueDirection =
    if (this == ClueDirection.ACROSS) ClueDirection.DOWN else ClueDirection.ACROSS

private fun buildCells(puzzle: Puzzle, givenCells: Set<CellPos>): Map<CellPos, CrosswordCellState> {
    val solution = Array(puzzle.height) { arrayOfNulls<Char>(puzzle.width) }
    fun place(row: Int, col: Int, dr: Int, dc: Int, answer: String) {
        for (i in answer.indices) {
            solution[row + dr * i][col + dc * i] = answer[i]
        }
    }
    puzzle.cluesAcross.forEach { place(it.row, it.col, 0, 1, it.answer) }
    puzzle.cluesDown.forEach { place(it.row, it.col, 1, 0, it.answer) }

    val numbers = mutableMapOf<CellPos, Int>()
    (puzzle.cluesAcross + puzzle.cluesDown).forEach { numbers[CellPos(it.row, it.col)] = it.number }

    val result = LinkedHashMap<CellPos, CrosswordCellState>()
    for (r in 0 until puzzle.height) {
        for (c in 0 until puzzle.width) {
            val pos = CellPos(r, c)
            val isBlack = puzzle.grid[r][c] == '#'
            val cellSolution = if (isBlack) null else solution[r][c]
            result[pos] = CrosswordCellState(
                pos = pos,
                isBlack = isBlack,
                solution = cellSolution,
                number = numbers[pos],
                // A starter with no solution behind it (a black square, or a hole
                // no clue covers) would open locked and permanently unsolvable.
                isGiven = pos in givenCells && cellSolution != null,
            )
        }
    }
    return result
}
